import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import PaymentProcessingModal from "../../modal/PaymentProcessingModal";
import TransactionStatusModal from "../../modal/TransactionStatusModal";
import { formatPaymentError } from "../../../utils/formatPaymentError";
import { isIgnorableAdyenErrorMessage } from "../../../utils/adyenStyleMapper";

const AdyenCardFormView = ({
  t,
  loading,
  error,
  cardError,
  sdkLoading,
  cardReady,
  initializationComplete,
  cardElementRef,
  threeDsElementRef,
  awaiting3DS,
  threeDsMode,
  showProcessingModal,
  setProcessingModal,
  showStatusModal,
  setShowStatusModal,
  transactionStatus,
  transactionId,
  checkoutConfDto,
  hostedPageRedirect,
  adyenSetupBlocked,
  adyenSetupError,
  configReady,
}) => {
  const overlayOpen = Boolean(awaiting3DS);

  useEffect(() => {
    if (!overlayOpen) {
      return undefined;
    }
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [overlayOpen]);

  return (
    <div
      className={`adyen-card-container${
        awaiting3DS ? " adyen-3ds-active" : ""
      }${threeDsMode === "challenge" ? " adyen-3ds-challenge" : ""}`}
    >
      <div className="form-section">
        {adyenSetupBlocked && (
          <div className="error-message">
            {adyenSetupError ||
              t("paymentForms.card.unavailable") ||
              "Card payments are not configured. Check the Adyen client key."}
          </div>
        )}

        {!initializationComplete && !adyenSetupBlocked && (
          <div className="initialization-loading">
            <div className="spinner-large" />
            <div className="loading-text">
              {t("paymentForms.card.initializing") ||
                "Initializing payment form..."}
            </div>
          </div>
        )}

        {initializationComplete && !adyenSetupBlocked && (
          <div className="card-mount-area" style={{ position: "relative" }}>
            {sdkLoading && (
              <div className="sdk-loading-text">
                {t("paymentForms.card.loadingSDK") ||
                  "Loading card form..."}
              </div>
            )}
            <div
              className={`card-mount-shell ${
                cardReady && !configReady ? "encryption-pending" : ""
              }`}
            >
              {cardReady && !configReady && (
                <div className="encryption-pending-overlay" aria-hidden="true">
                  {t("paymentForms.card.securingFields") ||
                    "Securing card fields…"}
                </div>
              )}
              <div
                ref={cardElementRef}
                className={`card-element-wrapper ${
                  !cardReady ? "loading" : ""
                } ${cardError ? "invalid" : ""}`}
              />
            </div>
          </div>
        )}
        {typeof document !== "undefined" &&
          createPortal(
            <div
              className={`adyen-3ds-overlay${overlayOpen ? " is-open" : ""}`}
              role="dialog"
              aria-modal={overlayOpen}
              aria-hidden={!overlayOpen}
              aria-label={
                t("paymentForms.card.authenticating")
              }
            >
              <div className="adyen-3ds-overlay__backdrop" />
              <div className="adyen-3ds-overlay__dialog">
                <div className="adyen-3ds-overlay__status" aria-live="polite">
                  <div className="spinner-large" />
                  <div className="loading-text">
                    {t("paymentForms.card.authenticating")}
                  </div>
                </div>
                <div
                  ref={threeDsElementRef}
                  className={`adyen-3ds-container${
                    overlayOpen ? " is-active" : ""
                  }${threeDsMode === "challenge" ? " is-challenge" : ""}`}
                />
              </div>
            </div>,
            document.body
          )}

        {cardError && !awaiting3DS && !isIgnorableAdyenErrorMessage(cardError) && (
          <div className="error-message">
            {formatPaymentError(cardError, t)}
          </div>
        )}
      </div>

      {/* General Error */}
      {error && !cardError && !awaiting3DS && (
        <div className="error-message">{error}</div>
      )}

      {/* Loader — never cover the native 3DS challenge */}
      {loading && !awaiting3DS && (
        <div className="loader-overlay">
          <div className="spinner-large" />
          <div className="loading-text">
            {t("paymentForms.card.processing")}
          </div>
        </div>
      )}

      <PaymentProcessingModal
        show={showProcessingModal}
        setShow={setProcessingModal}
      />

      <TransactionStatusModal
        show={showStatusModal}
        setShow={setShowStatusModal}
        status={transactionStatus}
        transactionId={transactionId}
        hostedPageRedirect={hostedPageRedirect}
        fallbackRedirectUrl={checkoutConfDto?.checkoutRedirectUrl}
      />
    </div>
  );
};

export default AdyenCardFormView;
